import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { API_TIME_OUT } from 'core/api'

abstract class HttpClient {
  protected readonly instance: AxiosInstance

  public constructor(baseURL: string) {
    this.instance = axios.create({
      baseURL,
      timeout: API_TIME_OUT,
    })

    this.initializeResponseInterceptor()
  }

  private initializeResponseInterceptor = () => {
    this.instance.interceptors.response.use(this.handleResponse, this.handleError)
  }

  private handleResponse = (response: AxiosResponse) => {
    return response
  }

  protected handleError = (error: unknown) => {
    return Promise.reject(error)
  }

  /**
   * HTTP GET method
   *
   * @access public
   * @template TRequest - request body 객체 타입
   * @template TResponse - axios response에 포함할 response data 객체 타입
   * @param {string} path - 엔드 포인트 경로
   * @param {import("axios").AxiosRequestConfig} [config] - axios request config
   * @returns {Promise<AxiosResponse>} HTTP `axios` response payload
   * @memberof Api
   */
  public get = <TResponse>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return this.instance.get<TResponse>(path, config)
  }

  /**
   * HTTP POST method
   *
   * @access public
   * @template TRequest - request body 객체 타입
   * @template TResponse - axios response에 포함할 response data 객체 타입
   * @param {string} path - 엔드 포인트 경로
   * @param {TRequest} data - request body 객체
   * @param {import("axios").AxiosRequestConfig} [config] - axios request config
   * @returns {Promise<AxiosResponse>} - HTTP `axios` response payload
   * @memberof Api
   */
  public post<TRequest>(
    path: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this.instance.post(path, data, config)
  }

  /**
   * HTTP PUT method
   *
   * @access public
   * @template TRequest - request body 객체 타입
   * @param {string} path - 엔드 포인트 경로
   * @param {TRequest} data - request body 객체
   * @param {import("axios").AxiosRequestConfig} [config] - axios request config
   * @returns {Promise<AxiosResponse>} - HTTP `axios` response payload
   * @memberof Api
   */
  public put<TRequest>(
    path: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this.instance.put(path, data, config)
  }

  /**
   * HTTP PATCH method
   *
   * @access public
   * @template TRequest - request body 객체 타입
   * @template TResponse - axios response에 포함할 response data 객체 타입
   * @param {string} path - 엔드 포인트 경로
   * @param {TRequest} data - request body 객체
   * @param {import("axios").AxiosRequestConfig} [config] - axios request config
   * @returns {Promise<AxiosResponse<TResponse>>} - HTTP `axios` response payload
   * @memberof Api
   */
  public patch<TRequest>(
    path: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this.instance.patch(path, data, config)
  }

  /**
   * HTTP DELETE method
   *
   * @access public
   * @param {string} path - 엔드 포인트 경로
   * @param {import("axios").AxiosRequestConfig} [config] - axios request config
   * @returns {Promise<AxiosResponse>} - HTTP [axios] response payload
   * @memberof Api
   */
  public delete(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.instance.delete(path, config)
  }
}

export default HttpClient
